import React, { useState, useEffect, useRef } from 'react';
import { FaDownload, FaExclamationTriangle } from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig';
import { Modal } from './common';
import { armarBienvenida } from './OnboardingWizard';

const CUENTAS = ['windows', 'email', 'profit', 'centroSura', 'cima', 'solucionesTi', 'catalogo'];

const descargar = (blob, nombre) => {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', nombre);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const GenerarBienvenidaModal = ({ isOpen, onClose, employee }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Si paso por el wizard, la imagen sale directo con lo que se anoto ahi
  const [desdeWizard, setDesdeWizard] = useState(Boolean(employee?.tiene_onboarding));
  const cerrar = useRef(onClose);
  cerrar.current = onClose;
  const [formData, setFormData] = useState({
    windows: {
      enabled: true,
      username: '',
      password: ''
    },
    email: {
      enabled: true,
      username: '',
      password: ''
    },
    profit: {
      enabled: false,
      username: '',
      password: ''
    },
    fuerzaMovil: {
      enabled: false,
      id: '',
      username: '',
      password: ''
    },
    dispositivoMovil: {
      enabled: false,
      linea: ''
    },
    pinImpresion: {
      enabled: false,
      pin: ''
    },
    centroSura: {
      enabled: false,
      username: '',
      password: ''
    },
    cima: {
      enabled: false,
      username: '',
      password: ''
    },
    solucionesTi: {
      enabled: false,
      username: '',
      password: ''
    }
  });
  const [emailDomain, setEmailDomain] = useState('sura.com.ve');

  // El formulario queda para quien entro antes del wizard o si el wizard no
  // dejo ninguna cuenta hecha.
  useEffect(() => {
    if (!employee?.tiene_onboarding) return undefined;
    let cancelado = false;
    (async () => {
      let cuerpo;
      try {
        const r = await axiosInstance.get(`/api/onboarding/${employee.id}`);
        cuerpo = armarBienvenida(r.data, (r.data.empleado.linea_sugerida || '').replace(/\D/g, ''));
      } catch (e) {
        cuerpo = null;
      }
      if (cancelado) return;
      if (!cuerpo || !CUENTAS.some((k) => cuerpo[k].enabled)) {
        setDesdeWizard(false);
        return;
      }
      try {
        const resp = await axiosInstance.post('/api/generar-bienvenida', cuerpo, { responseType: 'blob' });
        if (cancelado) return;
        descargar(resp.data, `bienvenida_${cuerpo.nombre_empleado.replace(/\s+/g, '_').toLowerCase()}.png`);
        cerrar.current();
      } catch (e) {
        if (cancelado) return;
        setError('No se pudo generar con los datos del wizard. Puedes llenarla a mano.');
        setDesdeWizard(false);
      }
    })();
    return () => { cancelado = true; };
  }, [employee?.id, employee?.tiene_onboarding]);

  useEffect(() => {
    if (employee) {
      const windowsUser = generateWindowsUsername(employee);
      const emailUser = generateEmailUsername(employee, emailDomain);

      setFormData(prev => ({
        ...prev,
        windows: {
          ...prev.windows,
          username: windowsUser
        },
        email: {
          ...prev.email,
          username: emailUser
        },
        dispositivoMovil: {
          ...prev.dispositivoMovil,
          linea: employee.linea || ''
        },
        centroSura: {
          ...prev.centroSura,
          username: windowsUser
        },
        cima: {
          ...prev.cima,
          username: windowsUser
        },
        solucionesTi: {
          ...prev.solucionesTi,
          username: windowsUser
        }
      }));
    }
  }, [employee, emailDomain]);

  const normalizeName = (str) => {
    if (!str) return '';
    return str
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/ñ/gi, 'n')
      .replace(/\s+/g, '')
      .toLowerCase();
  };

  const generateWindowsUsername = (empleado) => {
    const primerNombre = normalizeName(empleado?.primer_nombre);
    const apellido = normalizeName(empleado?.apellido);
    if (!primerNombre || !apellido) return '';
    return `${primerNombre.charAt(0)}${apellido}`;
  };

  const generateEmailUsername = (empleado, dominio) => {
    const primerNombre = normalizeName(empleado?.primer_nombre);
    const apellido = normalizeName(empleado?.apellido);
    if (!primerNombre || !apellido) return '';
    return `${primerNombre}.${apellido}@${dominio}`;
  };

  const handleCheckboxChange = (section) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        enabled: !prev[section].enabled
      }
    }));
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);

    try {
      const dataToSend = {
        ...formData,
        nombre_empleado: employee.nombre,
        primer_nombre: employee.primer_nombre || ''
      };

      const response = await axiosInstance.post('/api/generar-bienvenida', dataToSend, {
        responseType: 'blob'
      });

      descargar(response.data, `bienvenida_${employee.nombre.replace(/\s+/g, '_').toLowerCase()}.png`);

      onClose();
    } catch (error) {
      console.error('Error generando bienvenida:', error);
      setError('Error generando la imagen de bienvenida. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header onClose={onClose}>Generar Imagen de Bienvenida</Modal.Header>
      <Modal.Body>
        {loading || desdeWizard ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Generando imagen de bienvenida...</p>
          </div>
        ) : (
          <div className="bienvenida-form">
            <div className="form-section">
              <label>
                <input
                  type="checkbox"
                  checked={formData.windows.enabled}
                  onChange={() => handleCheckboxChange('windows')}
                />
                Usuario de Windows
              </label>
              {formData.windows.enabled && (
                <div className="section-inputs">
                  <input
                    type="text"
                    value={formData.windows.username}
                    onChange={(e) => handleInputChange('windows', 'username', e.target.value)}
                    placeholder="Usuario"
                    readOnly
                  />
                  <input
                    type="text"
                    value={formData.windows.password}
                    onChange={(e) => handleInputChange('windows', 'password', e.target.value)}
                    placeholder="Contraseña"
                  />
                </div>
              )}
            </div>

            <div className="form-section">
              <label>
                <input
                  type="checkbox"
                  checked={formData.email.enabled}
                  onChange={() => handleCheckboxChange('email')}
                />
                Correo Electrónico
              </label>
              {formData.email.enabled && (
                <div className="section-inputs">
                  <input
                    type="text"
                    value={formData.email.username}
                    onChange={(e) => handleInputChange('email', 'username', e.target.value)}
                    placeholder="Correo"
                    readOnly
                  />
                  <select
                    value={emailDomain}
                    onChange={(e) => setEmailDomain(e.target.value)}
                    style={{ marginLeft: '8px' }}
                  >
                    <option value="sura.com.ve">@sura.com.ve</option>
                    <option value="suraven.com">@suraven.com</option>
                  </select>
                  <input
                    type="text"
                    value={formData.email.password}
                    onChange={(e) => handleInputChange('email', 'password', e.target.value)}
                    placeholder="Contraseña"
                  />
                </div>
              )}
            </div>

            <div className="form-section">
              <label>
                <input
                  type="checkbox"
                  checked={formData.profit.enabled}
                  onChange={() => handleCheckboxChange('profit')}
                />
                Perfil de Profit
              </label>
              {formData.profit.enabled && (
                <div className="section-inputs">
                  <input
                    type="text"
                    value={formData.profit.username}
                    onChange={(e) => handleInputChange('profit', 'username', e.target.value)}
                    placeholder="Usuario"
                  />
                  <input
                    type="text"
                    value={formData.profit.password}
                    onChange={(e) => handleInputChange('profit', 'password', e.target.value)}
                    placeholder="Contraseña"
                  />
                </div>
              )}
            </div>

            <div className="form-section">
              <label>
                <input
                  type="checkbox"
                  checked={formData.fuerzaMovil.enabled}
                  onChange={() => handleCheckboxChange('fuerzaMovil')}
                />
                App Fuerza Móvil
              </label>
              {formData.fuerzaMovil.enabled && (
                <div className="section-inputs">
                  <input
                    type="text"
                    value={formData.fuerzaMovil.id}
                    onChange={(e) => handleInputChange('fuerzaMovil', 'id', e.target.value)}
                    placeholder="ID"
                  />
                  <input
                    type="text"
                    value={formData.fuerzaMovil.username}
                    onChange={(e) => handleInputChange('fuerzaMovil', 'username', e.target.value)}
                    placeholder="Usuario"
                  />
                  <input
                    type="text"
                    value={formData.fuerzaMovil.password}
                    onChange={(e) => handleInputChange('fuerzaMovil', 'password', e.target.value)}
                    placeholder="Contraseña"
                  />
                </div>
              )}
            </div>

            <div className="form-section">
              <label>
                <input
                  type="checkbox"
                  checked={formData.dispositivoMovil.enabled}
                  onChange={() => handleCheckboxChange('dispositivoMovil')}
                />
                Acceso a Dispositivo Móvil
              </label>
              {formData.dispositivoMovil.enabled && (
                <div className="section-inputs">
                  <input
                    type="text"
                    value={formData.dispositivoMovil.linea}
                    onChange={(e) => handleInputChange('dispositivoMovil', 'linea', e.target.value)}
                    placeholder="Número de línea"
                  />
                </div>
              )}
            </div>

            <div className="form-section">
              <label>
                <input
                  type="checkbox"
                  checked={formData.pinImpresion.enabled}
                  onChange={() => handleCheckboxChange('pinImpresion')}
                />
                PIN de Impresión
              </label>
              {formData.pinImpresion.enabled && (
                <div className="section-inputs">
                  <input
                    type="text"
                    value={formData.pinImpresion.pin}
                    onChange={(e) => handleInputChange('pinImpresion', 'pin', e.target.value)}
                    placeholder="PIN"
                  />
                </div>
              )}
            </div>

            <div className="form-section">
              <label>
                <input
                  type="checkbox"
                  checked={formData.centroSura.enabled}
                  onChange={() => handleCheckboxChange('centroSura')}
                />
                Usuario Centro Sura
              </label>
              {formData.centroSura.enabled && (
                <div className="section-inputs">
                  <input
                    type="text"
                    value={formData.centroSura.username}
                    onChange={(e) => handleInputChange('centroSura', 'username', e.target.value)}
                    placeholder="Usuario"
                  />
                  <input
                    type="text"
                    value={formData.centroSura.password}
                    onChange={(e) => handleInputChange('centroSura', 'password', e.target.value)}
                    placeholder="Contraseña"
                  />
                </div>
              )}
            </div>

            <div className="form-section">
              <label>
                <input
                  type="checkbox"
                  checked={formData.cima.enabled}
                  onChange={() => handleCheckboxChange('cima')}
                />
                Usuario Cima
              </label>
              {formData.cima.enabled && (
                <div className="section-inputs">
                  <input
                    type="text"
                    value={formData.cima.username}
                    onChange={(e) => handleInputChange('cima', 'username', e.target.value)}
                    placeholder="Usuario"
                  />
                  <input
                    type="text"
                    value={formData.cima.password}
                    onChange={(e) => handleInputChange('cima', 'password', e.target.value)}
                    placeholder="Contraseña"
                  />
                </div>
              )}
            </div>

            <div className="form-section">
              <label>
                <input
                  type="checkbox"
                  checked={formData.solucionesTi.enabled}
                  onChange={() => handleCheckboxChange('solucionesTi')}
                />
                Usuario Soluciones TI
              </label>
              {formData.solucionesTi.enabled && (
                <div className="section-inputs">
                  <input
                    type="text"
                    value={formData.solucionesTi.username}
                    onChange={(e) => handleInputChange('solucionesTi', 'username', e.target.value)}
                    placeholder="Usuario"
                  />
                  <input
                    type="text"
                    value={formData.solucionesTi.password}
                    onChange={(e) => handleInputChange('solucionesTi', 'password', e.target.value)}
                    placeholder="Contraseña"
                  />
                </div>
              )}
            </div>

            {error && (
              <div className="error-message">
                <FaExclamationTriangle />
                <p>{error}</p>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button onClick={onClose} className="btn btn-secondary">
          Cancelar
        </button>
        <button
          className="btn btn-primary"
          onClick={handleGenerate}
          disabled={loading}
        >
          <FaDownload className="mr-2" />
          Generar y Descargar
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default GenerarBienvenidaModal;